import { FunctionComponent, useState } from "react";
import { ConnectedProps, MapStateToProps, connect } from "react-redux";
import i18n from "../../i18n/index.js";
import { getNewsletterFieldSettings } from "../../selectors/modules.js";
import { getActiveSite } from "../../selectors/sites.js";
import {
  BaseModuleProps,
  ColorScheme,
  FormSubmitState,
  NewsletterModuleFieldSettings,
  NewsletterModuleSettings,
  StoreState,
} from "../../types/index.js";
import {
  getActiveColorScheme,
  getTranslations,
  scrollToElSingle,
} from "../../utils/utils.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";
import NewsletterForm from "../NewsletterForm.js";
import RichEditorWrapper from "../RichEditorWrapper.js";

type Props = BaseModuleProps<NewsletterModuleSettings>;

type ReduxProps = ConnectedProps<typeof connector>;

interface StateProps {
  scheme: ColorScheme;
  fieldSettings: NewsletterModuleFieldSettings;
}

const NewsletterModule: FunctionComponent<Props & ReduxProps> = ({
  translatedModule,
  translatedModule: {
    id,
    siteId,
    settings: { textAlign, layout },
    translation: {
      languageId,
      settings: { title, subtitle },
    },
  },
  isFirstOnPage,
  scheme,
  isPreview,
  pageId,
  fieldSettings,
  queries,
}) => {
  const [submitState, setSubmitState] = useState<FormSubmitState>("initial");

  const trans = getTranslations(languageId, i18n);
  const formId = `newsletter-${id}`;

  const showFieldBackgrounds = layout === "layout-2" || layout === "layout-4";
  const isWideLayout = layout === "layout-3" || layout === "layout-4";

  return (
    <ModuleWithHeadings
      title={title}
      subtitle={subtitle}
      id={id}
      className="NewsletterModule"
      colors={{ background: scheme.main.background, color: scheme.main.text }}
    >
      <div className="Module__Wrapper">
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />

        <RichEditorWrapper
          module={translatedModule}
          pageId={pageId}
          languageId={languageId}
          isPreview={isPreview}
          scheme={scheme}
          className={`TextAlign--${textAlign.description}`}
        />
      </div>
      <div className="Module__Wrapper">
        {submitState !== "submitted" && (
          <NewsletterForm
            formId={formId}
            languageId={languageId}
            siteId={siteId}
            trans={trans}
            submitState={submitState}
            setSubmitState={setSubmitState}
            isPreview={isPreview}
            fieldSettings={fieldSettings}
            scheme={scheme}
            showFieldBackgrounds={showFieldBackgrounds}
            isWideLayout={isWideLayout}
            queries={queries}
          />
        )}
        {submitState === "submitted" && (
          <p
            ref={(el) => {
              el && scrollToElSingle(el);
            }}
            className="NewsletterModule__Message"
          >
            {trans.newsletterMessages.success}
          </p>
        )}
        {submitState === "error" && (
          <p className="NewsletterModule__Message">
            {trans.newsletterMessages.error}
          </p>
        )}
      </div>
    </ModuleWithHeadings>
  );
};

const mapStateToProps: MapStateToProps<StateProps, Props, StoreState> = (
  { colorSchemes, sites, modules },
  { translatedModule },
): StateProps => ({
  scheme: getActiveColorScheme(
    colorSchemes,
    getActiveSite(sites),
    translatedModule,
  ),
  fieldSettings: getNewsletterFieldSettings(
    modules,
    translatedModule.translation.languageId,
  ),
});

const connector = connect(mapStateToProps);

export default connector(NewsletterModule);
